import Alert from "react-s-alert";
import * as React from "react";
import { NotificationAlert } from "../components/common";

export type NotificationOptions = {
    content: React.ReactNode;
    icon?: any;
    timeout?: number;
    position?: "top-left" | "top-right" | "bottom-left" | "bottom-right" | "top" | "bottom";
    effect?: "slide" | "scale" | "bouncyflip" | "flip" | "genie" | "jelly" | "stackslide";
    onClose?: () => void;
    offset?: number;
    customFields?: any;
    minWidth?: number;
    maxWidth?: number;
};

/**
 * Notification service that wraps react-s-alert.
 * Provides a consistent API for displaying toast notifications throughout the application.
 * 
 * Benefits:
 * - Single point of change if we switch notification libraries in the future
 * - Consistent styling via NotificationAlert component
 * - Type-safe API
 * - Simplified usage in components
 * 
 * @example
 * // Simple string message
 * notificationService.success("Operation completed!");
 * 
 * @example
 * // With icon and custom content
 * notificationService.error({
 *   content: <span>Failed to save trial</span>,
 *   icon: FontAwesomeIcons.Solid.BAN
 * });
 */
export const notificationService = {
    /**
     * Display a success notification (green/positive feedback)
     */
    success: (options: NotificationOptions | string): string => {
        const config = typeof options === "string" 
            ? { content: <span>{options}</span> }
            : options;
        
        return Alert.success(
            <NotificationAlert
                alertContent={config.content}
                icon={config.icon}
                minWidth={config.minWidth}
                maxWidth={config.maxWidth}
            />,
            {
                timeout: config.timeout,
                position: config.position,
                effect: config.effect,
                onClose: config.onClose,
                offset: config.offset,
                customFields: config.customFields,
            }
        );
    },

    /**
     * Display an error notification (red/negative feedback)
     */
    error: (options: NotificationOptions | string): string => {
        const config = typeof options === "string" 
            ? { content: <span>{options}</span> }
            : options;
        
        return Alert.error(
            <NotificationAlert
                alertContent={config.content}
                icon={config.icon}
                minWidth={config.minWidth}
                maxWidth={config.maxWidth}
            />,
            {
                timeout: config.timeout,
                position: config.position,
                effect: config.effect,
                onClose: config.onClose,
                offset: config.offset,
                customFields: config.customFields,
            }
        );
    },

    /**
     * Display a warning notification (yellow/caution feedback)
     */
    warning: (options: NotificationOptions | string): string => {
        const config = typeof options === "string" 
            ? { content: <span>{options}</span> }
            : options;
        
        return Alert.warning(
            <NotificationAlert
                alertContent={config.content}
                icon={config.icon}
                minWidth={config.minWidth}
                maxWidth={config.maxWidth}
            />,
            {
                timeout: config.timeout,
                position: config.position,
                effect: config.effect,
                onClose: config.onClose,
                offset: config.offset,
                customFields: config.customFields,
            }
        );
    },

    /**
     * Display an info notification (blue/informational feedback)
     */
    info: (options: NotificationOptions | string): string => {
        const config = typeof options === "string" 
            ? { content: <span>{options}</span> }
            : options;
        
        return Alert.info(
            <NotificationAlert
                alertContent={config.content}
                icon={config.icon}
                minWidth={config.minWidth}
                maxWidth={config.maxWidth}
            />,
            {
                timeout: config.timeout,
                position: config.position,
                effect: config.effect,
                onClose: config.onClose,
                offset: config.offset,
                customFields: config.customFields,
            }
        );
    },

    /**
     * Close a specific notification by ID, or close all notifications
     */
    close: (id?: string): void => {
        if (id) {
            Alert.close(id);
        } else {
            Alert.closeAll();
        }
    },

    /**
     * Close all notifications
     */
    closeAll: (): void => {
        Alert.closeAll();
    },
};
